import { createContext, useContext, useEffect, useState } from 'react';
import Cookies from 'universal-cookie';
import { DBRequest, useConfig } from '../../api/api';

const EmailClient = createContext({
  preferences: null,
  updateSelectedFolder: () => {},
});

export const EmailClientProvider = ({ children }) => {
  const config = useConfig();
  const [apiData, setApiData] = useState(null);
  const [unseenCount, setUnseenCount] = useState(null);
  const [failedCount, setFailedCount] = useState(null);
  const [queuedCount, setQueuedCount] = useState(null);
  // drafts prepared by AI agents that wait for the user's review
  const [aiDraftCount, setAiDraftCount] = useState(null);
  // inboxes whose connection is broken and need re-authentication
  const [reauthInboxes, setReauthInboxes] = useState([]);
  // The vacation driven auto reply. Kept here rather than in the settings
  // dialog so the inbox list can mark the mailboxes it answers for without
  // opening settings first. null = not loaded yet.
  const [vacationNotification, setVacationNotification] = useState(null);

  // update clock
  const updateInSeconds = 10;
  useEffect(() => {
    // INITIAL FETCH
    if (!apiData) fetchFolders();

    // Set an interval to fetch folders every `updateInSeconds` seconds
    const requestMails = setInterval(() => {
      if (apiData?.data.length > 0) fetchFolders();
    }, updateInSeconds * 1000);
    return () => clearInterval(requestMails);
  }, [config, apiData]);

  // Listen for standalone auth changes (re-login)
  useEffect(() => {
    const handleAuthChange = (event) => {
      if (event.detail?.authenticated && config) {
        // Small delay to ensure cookies are set
        setTimeout(() => {
          fetchFolders();
        }, 100);
      }
    };

    window.addEventListener('standaloneAuthChange', handleAuthChange);
    return () => window.removeEventListener('standaloneAuthChange', handleAuthChange);
  }, [config]);

  function fetchFolders() {
    // No session (public pages) — skip the authenticated request.
    if (!config || !new Cookies().get('accessToken')) return;
    DBRequest({
      config,
      path: `email-credentials`,
      onResponse: handleResponse,
    });
  }

  // Deliberately not part of the folder poll above — this changes only when the
  // user edits it, so it is read once and then kept in sync by the settings tab.
  function fetchVacationNotification() {
    // No session (public pages) — skip the authenticated request.
    if (!config || !new Cookies().get('accessToken')) return;
    DBRequest({
      config,
      path: 'absence-notifications/vacation',
      onResponse: (res) =>
        setVacationNotification(res?.data?.data || res?.data || {}),
      // An empty object still resolves the loading state; with no `isEnabled`
      // it simply marks nothing.
      onError: () => setVacationNotification({}),
    });
  }

  useEffect(() => {
    if (config) fetchVacationNotification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  function handleResponse(response) {
    setApiData(response);
    setUnseenCount(getUnseenCount(response.data));
    setFailedCount(sumCredentialField(response.data, 'failedEmailCount'));
    setQueuedCount(sumCredentialField(response.data, 'queuedEmailCount'));
    setAiDraftCount(sumCredentialField(response.data, 'pendingAiDraftCount'));
    setReauthInboxes(getReauthInboxes(response.data));
  }

  // credentials whose IMAP connection is no longer authenticated (imapStatus !== 1)
  function getReauthInboxes(arrOfInboxes) {
    return arrOfInboxes?.filter((inbox) => inbox.imapStatus !== 1) || [];
  }

  function getUnseenCount(arrOfInboxes) {
    var count = 0;
    arrOfInboxes.forEach((element) => {
      const folders = element.inbox.folders;
      folders?.forEach((folder) => {
        if (folder.name === 'INBOX') count += folder.unseenCount;
      });
    });
    return count;
  }

  function sumCredentialField(credentials, field) {
    return credentials.reduce((sum, cred) => sum + (cred[field] || 0), 0);
  }

  return (
    <EmailClient.Provider
      value={{
        emailCredentials: apiData,
        setEmailCredentials: setApiData,
        fetchFolders,
        unseenCount,
        failedCount,
        queuedCount,
        aiDraftCount,
        reauthInboxes,
        reauthCount: reauthInboxes.length,
        vacationNotification,
        setVacationNotification,
        fetchVacationNotification,
      }}
    >
      {children}
    </EmailClient.Provider>
  );
};

export const useEmailClient = () => useContext(EmailClient);
